import { useTicketUpdate } from 'features/libra/ticket/TicketUpdateContext'
import {
  LibraWorflowStepComponentProps,
  type UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import { useGeckoNotifications } from 'features/notifications'
import { FC, useMemo } from 'react'
import styled from 'styled-components'
import { GeckoFileCategory } from 'types'
import { z } from 'zod'

import { DocumentCoverageTable } from './niiDataHealth/DocumentCoverageTable'
import { useDataHealthMetrics } from './niiDataHealth/useDataHealthMetrics'

// config
export const niiDataHealthConfigSchema = z.object({
  requiredCategories: z
    .array(z.nativeEnum(GeckoFileCategory))
    .optional()
    .transform((categories) => categories ?? []),
})

export type NiiDataHealthConfig = z.infer<typeof niiDataHealthConfigSchema>

export const Component: FC<
  LibraWorflowStepComponentProps<NiiDataHealthConfig>
> = ({ config }) => {
  const { ticketTags } = useTicketUpdate()
  const unitId = ticketTags.unit as string | undefined
  const { errorNotification } = useGeckoNotifications()

  const parsedConfig = useMemo(() => {
    const result = niiDataHealthConfigSchema.safeParse(config)
    if (!result.success) {
      errorNotification('Invalid NiiDataHealth config')
      return { requiredCategories: [] }
    }
    return result.data
  }, [config, errorNotification])

  const { categoryMetrics, totalDocuments, isLoading, hasEverLoaded } =
    useDataHealthMetrics(unitId)

  if (!unitId) {
    return (
      <CenteredMessage $color={'#999'}>
        No unit specified for this ticket. Please ensure the ticket is tagged
        with a unit.
      </CenteredMessage>
    )
  }

  if (isLoading && !hasEverLoaded) {
    return <CenteredMessage>Loading document data...</CenteredMessage>
  }

  return (
    <DocumentCoverageTable
      metrics={categoryMetrics}
      totalDocuments={totalDocuments}
      requiredCategories={parsedConfig.requiredCategories}
      isLoading={isLoading}
    />
  )
}

export const useValidateConfig: UseValidateConfigHook = ({ config }) => {
  return useMemo(() => {
    return niiDataHealthConfigSchema.safeParse(config).success
  }, [config])
}

const CenteredMessage = styled.div<{ $color?: string }>`
  padding: 2rem;
  text-align: center;
  color: ${({ $color = 'inherit' }) => $color};
`
